import { useDataProvider } from "ra-core";
import { useEffect, useRef, useState } from "react";

export type SaveState = "idle" | "saving" | "saved" | "error";

/**
 * Saves `data` to the row a second after the last change. `ready` stays false
 * until the row is loaded so the first render does not write it back.
 */
export const useAutosave = (
  resource: string,
  id: number | string | undefined,
  data: Record<string, unknown> | null,
  ready: boolean,
) => {
  const dataProvider = useDataProvider();
  const [state, setState] = useState<SaveState>("idle");
  const last = useRef<string | null>(null);
  const serialized = data ? JSON.stringify(data) : null;

  useEffect(() => {
    if (!ready || id == null || !serialized) return;
    if (last.current === null) {
      last.current = serialized;
      return;
    }
    if (serialized === last.current) return;
    setState("saving");
    const timer = setTimeout(async () => {
      try {
        await dataProvider.update(resource, {
          id,
          data: JSON.parse(serialized),
          previousData: { id },
        });
        last.current = serialized;
        setState("saved");
      } catch (error) {
        console.error(error);
        setState("error");
      }
    }, 900);
    return () => clearTimeout(timer);
  }, [dataProvider, id, ready, resource, serialized]);

  return state;
};

export const SAVE_LABEL: Record<SaveState, string> = {
  idle: "",
  saving: "Guardando…",
  saved: "Guardado",
  error: "No se ha podido guardar",
};
